import React from "react";
import { motion } from "framer-motion";
import { Truck, Plane, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import {
  buildWhatsAppLink,
  WHATSAPP_DEFAULT_MSG_EN,
  WHATSAPP_DEFAULT_MSG_ES,
} from "../config";

// WhatsApp brand SVG
const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const RelocationBanner = () => {
  const { t, lang } = useLang();
  const link = buildWhatsAppLink(
    lang === "es" ? WHATSAPP_DEFAULT_MSG_ES : WHATSAPP_DEFAULT_MSG_EN
  );

  return (
    <section data-testid="relocation-banner" className="relative bg-brand-cream py-6 sm:py-8 px-5 sm:px-8 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative max-w-5xl mx-auto rounded-3xl bg-white border border-brand-navy/10 p-5 sm:p-7 shadow-[0_15px_40px_-20px_rgba(14,42,86,0.25)] overflow-hidden"
      >
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-brand-red/10 blur-2xl" aria-hidden="true" />

        <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-7">
          <div className="flex-shrink-0 flex items-center gap-2">
            <div className="w-12 h-12 rounded-2xl bg-brand-red text-white flex items-center justify-center">
              <Truck size={22} strokeWidth={2.2} />
            </div>
            <div className="w-12 h-12 rounded-2xl bg-brand-red text-white flex items-center justify-center">
              <Plane size={22} strokeWidth={2.2} />
            </div>
          </div>

          <div className="flex-1">
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-brand-red mb-1.5">
              {t.relocation.eyebrow}
            </div>
            <h3 className="font-heading font-bold text-lg sm:text-xl text-brand-navy leading-snug tracking-tight">
              {t.relocation.title}
            </h3>
            <p className="text-sm text-brand-navy/70 leading-relaxed mt-1.5 max-w-xl">
              {t.relocation.body}
            </p>
          </div>

          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="relocation-whatsapp-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-brand-navy text-white font-heading font-bold text-sm sm:text-base px-5 py-3 shadow-md hover:shadow-lg transition-all whitespace-nowrap"
          >
            <WhatsAppIcon size={18} />
            {t.relocation.cta}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </motion.div>
    </section>
  );
};

export default RelocationBanner;
